import { PLAN_PRICING, type PlanId } from "@biteasy/contracts";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
	ArrowDownRight,
	ArrowUpRight,
	BarChart3,
	Boxes,
	Crown,
	ExternalLink,
	Link2,
	Send,
	Users,
	X,
	Zap,
} from "lucide-react";
import { useState } from "react";
import { Badge, type BadgeProps } from "@/components/catalyst/Badge";
import { Button } from "@/components/catalyst/Button";
import { Heading, Subheading } from "@/components/catalyst/Heading";
import { Text } from "@/components/catalyst/Text";
import { orpc } from "@/orpc/client";
import { formatShortDate } from "@/util/date";
import { CancelSubscriptionDialog } from "./CancelSubscriptionDialog";
import { ChangePlanDialog } from "./ChangePlanDialog";

const PLAN_DISPLAY_NAMES: Record<PlanId, string> = {
	bootstrap: "Bootstrap",
	standard: "Standard",
	scale: "Scale",
	enterprise: "Enterprise",
};

const PLAN_BADGE_COLORS: Record<PlanId, BadgeProps["color"]> = {
	bootstrap: "lime",
	standard: "amber",
	scale: "purple",
	enterprise: "sky",
};

function getStatusBadge(
	status: string,
	isActive: boolean,
	plan: PlanId,
): { color: BadgeProps["color"]; label: string } {
	switch (status) {
		case "past_due":
			return { color: "amber", label: "Past Due" };
		case "unpaid":
			return { color: "red", label: "Unpaid" };
		default:
			return isActive
				? { color: "lime", label: "Active" }
				: { color: PLAN_BADGE_COLORS[plan], label: status };
	}
}

const PAID_PLANS = ["bootstrap", "standard", "scale"] as const;

export function SubscriptionSettings() {
	const navigate = useNavigate();

	const [changePlanTarget, setChangePlanTarget] = useState<
		"bootstrap" | "standard" | "scale" | null
	>(null);
	const [showCancelDialog, setShowCancelDialog] = useState(false);

	const { data: subscription } = useSuspenseQuery(
		orpc.billing.getSubscription.queryOptions(),
	);

	const handleUpgrade = (plan: "bootstrap" | "standard" | "scale") => {
		navigate({
			to: "/checkout",
			search: { plan, billingPeriod: "monthly" },
		});
	};

	if (!subscription) {
		return (
			<div className="text-center p-6 border border-dashed border-base-content/10 rounded-lg">
				<Text>Unable to load subscription details.</Text>
			</div>
		);
	}

	const plan = subscription.plan as PlanId;
	const isActive =
		subscription.status === "active" ||
		subscription.status === "trialing" ||
		subscription.status === "past_due";

	const statusBadge = getStatusBadge(subscription.status, isActive, plan);
	const isCanceled =
		subscription.status === "canceled" ||
		subscription.status === "incomplete_expired";

	return (
		<div className="space-y-8">
			<div>
				<Heading>Subscription</Heading>
				<Subheading>Manage your subscription and view usage.</Subheading>
			</div>

			{/* Current Plan Card */}
			<div className="p-6 rounded-xl border border-base-content/10 bg-base-200/50">
				<div className="flex items-center justify-between flex-wrap gap-4">
					<div className="flex items-center gap-4">
						<div className="p-3 rounded-xl bg-primary/10">
							{plan === "enterprise" ? (
								<Crown className="size-6 text-primary" />
							) : (
								<Zap className="size-6 text-primary" />
							)}
						</div>
						<div>
							<div className="flex items-center gap-2">
								<Heading level={3}>{PLAN_DISPLAY_NAMES[plan]} Plan</Heading>
								<Badge color={statusBadge.color}>{statusBadge.label}</Badge>
							</div>
							{subscription.currentPeriodEnd && (
								<Text className="text-sm">
									{isActive ? "Renews" : "Expires"} on{" "}
									{formatShortDate(subscription.currentPeriodEnd)}
								</Text>
							)}
						</div>
					</div>

					{isActive && plan !== "enterprise" && (
						<div className="flex flex-wrap gap-2">
							{PAID_PLANS.filter((p) => p !== plan).map((targetPlan) => {
								const isUpgrade =
									PAID_PLANS.indexOf(targetPlan) >
									PAID_PLANS.indexOf(plan as (typeof PAID_PLANS)[number]);
								const isScale = targetPlan === "scale";
								return (
									<Button
										key={targetPlan}
										color={
											isScale ? "indigo" : isUpgrade ? "blue" : "dark/white"
										}
										onClick={() => setChangePlanTarget(targetPlan)}
									>
										{isUpgrade ? (
											<ArrowUpRight className="size-4" />
										) : (
											<ArrowDownRight className="size-4" />
										)}
										{isUpgrade ? "Upgrade" : "Change"} to{" "}
										{PLAN_DISPLAY_NAMES[targetPlan]}
									</Button>
								);
							})}
							<Button
								color="dark/white"
								onClick={() => setShowCancelDialog(true)}
							>
								<X className="size-4" />
								Cancel Subscription
							</Button>
						</div>
					)}
				</div>
			</div>

			{/* Usage Section */}
			<div>
				<Heading level={3} className="mb-4">
					Current Usage
				</Heading>
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
					<UsageCard
						icon={Boxes}
						iconColor="text-blue-400"
						iconBg="bg-blue-500/10"
						label="Apps"
						current={subscription.usage.apps}
						limit={subscription.limits.apps}
					/>
					<UsageCard
						icon={Users}
						iconColor="text-purple-400"
						iconBg="bg-purple-500/10"
						label="Partners"
						current={subscription.usage.partners}
						limit={subscription.limits.partners}
					/>
					<UsageCard
						icon={Link2}
						iconColor="text-emerald-400"
						iconBg="bg-emerald-500/10"
						label="Links"
						current={subscription.usage.links}
						limit={subscription.limits.links}
					/>
					<UsageCard
						icon={BarChart3}
						iconColor="text-amber-400"
						iconBg="bg-amber-500/10"
						label="Monthly Attributions"
						current={subscription.usage.claims}
						limit={subscription.limits.claims}
					/>
					<UsageCard
						icon={Send}
						iconColor="text-rose-400"
						iconBg="bg-rose-500/10"
						label="Monthly Payout Volume"
						current={subscription.usage.payoutVolumeCents}
						limit={subscription.limits.payoutCreditsCents}
						formatAsDollars
					/>
				</div>
			</div>

			{/* Resubscribe Banner for Canceled Users */}
			{isCanceled && (
				<div className="p-6 rounded-xl border border-warning/20 bg-warning/5">
					<Heading level={3} className="mb-2">
						Your subscription has been canceled
					</Heading>
					<Text className="mb-4">
						You have read-only access. Resubscribe to create apps, partners, and
						links again.
					</Text>
					<div className="flex flex-wrap gap-3">
						<Button
							onClick={() => handleUpgrade("standard")}
							color="dark/white"
						>
							<ArrowUpRight className="size-4" />
							Standard - ${PLAN_PRICING.standard.monthly}/mo
						</Button>
						<Button onClick={() => handleUpgrade("scale")}>
							<ArrowUpRight className="size-4" />
							Scale - ${PLAN_PRICING.scale.monthly}/mo
						</Button>
					</div>
				</div>
			)}

			{/* Enterprise CTA - Show for all non-enterprise users */}
			{plan !== "enterprise" && (
				<div className="py-5 px-6 rounded-xl border border-purple-500/20 bg-linear-to-b from-purple-500/5 via-base-200/50 to-base-200/30">
					<div className="flex items-center justify-between flex-wrap gap-4">
						<div>
							<Heading level={3}>Need more?</Heading>
							<Text className="text-base-content/70">
								Get custom limits, dedicated support, and enterprise features
								tailored to your needs.
							</Text>
						</div>
						<a
							href="https://biteasy.co/contact"
							target="_blank"
							rel="noopener noreferrer"
						>
							<Button color="dark/white">
								<ExternalLink className="size-4" />
								Contact Sales
							</Button>
						</a>
					</div>
				</div>
			)}

			{/* Plan Change Dialog */}
			<ChangePlanDialog
				isOpen={changePlanTarget !== null}
				onClose={() => setChangePlanTarget(null)}
				currentPlan={plan}
				targetPlan={changePlanTarget ?? "standard"}
			/>

			{/* Cancel Subscription Dialog */}
			<CancelSubscriptionDialog
				isOpen={showCancelDialog}
				onClose={() => setShowCancelDialog(false)}
			/>
		</div>
	);
}

function UsageCard({
	icon: Icon,
	iconColor,
	iconBg,
	label,
	current,
	limit,
	formatAsDollars,
}: {
	icon: LucideIcon;
	iconColor: string;
	iconBg: string;
	label: string;
	current: number;
	limit: number;
	formatAsDollars?: boolean;
}) {
	const isUnlimited = limit === Infinity || limit >= Number.MAX_SAFE_INTEGER;
	const percentage = isUnlimited ? 0 : (current / limit) * 100;
	const remaining = isUnlimited ? Infinity : Math.max(0, limit - current);

	const fmt = (value: number) =>
		formatAsDollars
			? `$${Math.round(value / 100).toLocaleString()}`
			: value.toLocaleString();

	return (
		<div className="p-5 rounded-xl border border-base-content/10 bg-base-100">
			{/* Icon */}
			<div className={`inline-flex p-2.5 rounded-lg ${iconBg} mb-4`}>
				<Icon className={`size-5 ${iconColor}`} />
			</div>

			{/* Label */}
			<Text className="text-sm text-base-content/60 mb-1">{label}</Text>

			{/* Value */}
			<div className="flex items-baseline gap-1 mb-3">
				<span className="text-3xl font-bold text-base-content">
					{fmt(current)}
				</span>
				<span className="text-base text-base-content/40">
					/ {isUnlimited ? "∞" : fmt(limit)}
				</span>
			</div>

			{/* Progress bar */}
			{!isUnlimited && (
				<div className="h-1 rounded-full bg-base-content/10 overflow-hidden mb-3">
					<div
						className="h-full rounded-full bg-blue-500 transition-all"
						style={{ width: `${Math.min(percentage, 100)}%` }}
					/>
				</div>
			)}

			{/* Stats row */}
			<div className="flex items-center justify-between text-xs">
				<span className="text-blue-400">{Math.round(percentage)}% used</span>
				<span className="text-base-content/40">
					{isUnlimited ? "∞" : fmt(remaining)} remaining
				</span>
			</div>
		</div>
	);
}
